import type { ReactNode } from 'react'
import { Box, Crosshair, Map, Mountain, MountainSnow, Route, Square } from 'lucide-react'
import { IconButton, ToggleGroup, Tooltip } from '@/components/play'
import type { MapboxBasemap } from './CourseMap'
import './course-map-controls.css'

/** The play world's switch while the Mapbox map shows: chosen and building, ready to pick, or unavailable and why. */
export interface CourseMapWorldSwitch {
  /** The world is the viewer's choice and is still being built; the Mapbox map stands in meanwhile. */
  building: boolean
  /** Why the world can't show, or null. */
  problem: string | null
  onSelect(): void
}

export interface CourseMapControlsProps {
  basemap: MapboxBasemap
  is3D: boolean
  hasRoute: boolean
  onBasemapChange(value: MapboxBasemap): void
  onDimensionChange(value: boolean): void
  onFitRoute(): void
  onRecenter(): void
  /** Set when the event's play world is on: a World switch leads the basemaps. */
  world?: CourseMapWorldSwitch
  /** The athlete search (AthleteSearch), leading the cluster while the map has athletes. */
  search?: ReactNode
}

function worldLabel({ building, problem }: CourseMapWorldSwitch) {
  if (problem) return `World unavailable: ${problem}`
  return building ? 'World, building' : 'World'
}

/** Shared Play controls for the Watch and destination maps. */
export function CourseMapControls({ basemap, is3D, hasRoute, onBasemapChange, onDimensionChange, onFitRoute, onRecenter, world, search }: CourseMapControlsProps) {
  // While the world is the choice and still building, no Mapbox basemap is the viewer's pick: pressing one picks it.
  const worldChosen = Boolean(world?.building)
  return (
    <div className="lv-map-controls course-map-controls" role="group" aria-label="Map controls"
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}>
      {search}
      {world && (
        <IconButton label={worldLabel(world)} elevated className="course-map-controls__world"
          aria-busy={world.building || undefined}
          aria-disabled={world.problem ? true : undefined}
          onClick={() => {
            if (!world.problem) world.onSelect()
          }}>
          <MountainSnow />
        </IconButton>
      )}
      <ToggleGroup label="Basemap" hideLabel iconOnly allowNone value={worldChosen ? null : basemap}
        onValueChange={(value) => {
          if (value) onBasemapChange(value)
        }}
        items={[
          { value: 'standard', label: 'Outdoors basemap', icon: <Mountain /> },
          { value: 'satellite', label: 'Satellite basemap', icon: <Map /> },
        ]} />
      <ToggleGroup label="Dimensionality" hideLabel iconOnly value={is3D ? '3d' : '2d'} onValueChange={(value) => onDimensionChange(value === '3d')}
        items={[
          { value: '2d', label: 'Flatten to 2D', icon: <Square /> },
          { value: '3d', label: 'Tilt to 3D', icon: <Box /> },
        ]} />
      <Tooltip content={hasRoute ? 'Zoom to course route' : 'No course route available'}>
        <span className="course-map-controls__route" tabIndex={hasRoute ? undefined : 0}>
          <IconButton label="Zoom to course route" tooltip={false} elevated disabled={!hasRoute} onClick={onFitRoute}>
            <Route />
          </IconButton>
        </span>
      </Tooltip>
      <IconButton label="Recenter cameras and route" elevated onClick={onRecenter}>
        <Crosshair />
      </IconButton>
    </div>
  )
}
